// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid,
  InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../utils/api';
import { NOT_PROVIDED, dateFr, billingErrorMessage } from '../../utils/formatFacturation';
import { PUBLIC_ENTITY_KINDS, Empty } from './commun';

/**
 * Onglet « Mandats SEPA » : mandats de prélèvement d'une organisation, leur
 * signature, leurs incidents et leur révocation. Un établissement public ne se
 * prélève pas : la création n'y est pas proposée.
 */
export default function MandatesTab({ orgs }) {
  const [orgId, setOrgId] = useState('');
  const [mandates, setMandates] = useState([]);
  const [identity, setIdentity] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogue, setDialogue] = useState(null); // {type, mandat}
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const charger = useCallback(async (id) => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/invoices/mandats', { params: id ? { organisation_id: id } : {} });
      setMandates(r.data.mandats || []);
    } catch (e) {
      setError(billingErrorMessage(e, 'Mandats illisibles'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(orgId); }, [orgId, charger]);

  useEffect(() => {
    let alive = true;
    if (!orgId) { setIdentity(null); return undefined; }
    api.get(`/invoices/organisations/${orgId}/identite`)
      .then((r) => { if (alive) setIdentity(r.data); })
      .catch(() => { if (alive) setIdentity(null); });
    return () => { alive = false; };
  }, [orgId]);

  const isPublic = PUBLIC_ENTITY_KINDS.includes(identity?.entity_kind);
  const unknownEntityKind = Boolean(orgId) && !identity?.entity_kind;

  const openItem = (type, mandate) => {
    setDialogue({ type, mandat: mandate });
    setDialogError('');
    if (type === 'creer') setForm({ debtor_name: '', site_id: '', rum: '', scheme: 'CORE', debtor_iban_last4: '', debtor_bic: '', creditor_ics: '', notes: '' });
    else if (type === 'signer') setForm({ signed_at: '', signature_place: '', document_key: '', creditor_ics: mandate?.creditor_ics || '' });
    else if (type === 'incident') setForm({ kind: '', reason: '', invoice_id: '', suspendre: false });
    else if (type === 'revoquer') setForm({ reason: '' });
  };

  const validate = async () => {
    setSending(true); setDialogError('');
    try {
      if (dialogue.type === 'creer') {
        const body = { organisation_id: orgId, debtor_name: form.debtor_name, scheme: form.scheme };
        if (form.site_id) body.site_id = form.site_id;
        if (form.rum) body.rum = form.rum;
        if (form.debtor_iban_last4) body.debtor_iban_last4 = form.debtor_iban_last4;
        if (form.debtor_bic) body.debtor_bic = form.debtor_bic;
        if (form.creditor_ics) body.creditor_ics = form.creditor_ics;
        if (form.notes) body.notes = form.notes;
        await api.post('/invoices/mandats', body);
      } else if (dialogue.type === 'signer') {
        const body = {};
        if (form.signed_at) body.signed_at = new Date(form.signed_at).toISOString();
        if (form.signature_place) body.signature_place = form.signature_place;
        if (form.document_key) body.document_key = form.document_key;
        if (form.creditor_ics) body.creditor_ics = form.creditor_ics;
        await api.post(`/invoices/mandats/${dialogue.mandat.id}/signer`, body);
      } else if (dialogue.type === 'incident') {
        const body = { kind: form.kind, suspendre: Boolean(form.suspendre) };
        if (form.reason) body.reason = form.reason;
        if (form.invoice_id) body.invoice_id = form.invoice_id;
        await api.post(`/invoices/mandats/${dialogue.mandat.id}/incident`, body);
      } else if (dialogue.type === 'revoquer') {
        await api.post(`/invoices/mandats/${dialogue.mandat.id}/revoquer`, { reason: form.reason });
      }
      setDialogue(null);
      charger(orgId);
    } catch (e) {
      setDialogError(billingErrorMessage(e, 'Opération refusée'));
    } finally {
      setSending(false);
    }
  };

  const dialogValid = (() => {
    if (!dialogue) return false;
    if (dialogue.type === 'creer') {
      const l4 = form.debtor_iban_last4 || '';
      return (form.debtor_name || '').trim().length >= 2 && (l4 === '' || l4.length === 4);
    }
    if (dialogue.type === 'incident') return (form.kind || '').trim().length >= 2;
    if (dialogue.type === 'revoquer') return (form.reason || '').trim().length >= 3;
    return true;
  })();

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Organisation</InputLabel>
          <Select value={orgId} label="Organisation" onChange={(e) => setOrgId(e.target.value)}>
            <MenuItem value="">Toutes</MenuItem>
            {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title={!orgId
          ? 'Choisir une organisation'
          : isPublic
            ? 'Un établissement public ne se prélève pas : la dépense est payée par virement du comptable public après service fait'
            : unknownEntityKind
              ? 'Nature juridique inconnue : la renseigner avant de créer un mandat'
              : ''}>
          <span>
            <Button variant="contained" size="small" startIcon={<AddIcon />}
              disabled={!orgId || isPublic || unknownEntityKind}
              onClick={() => openItem('creer', null)}>
              Nouveau mandat
            </Button>
          </span>
        </Tooltip>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => charger(orgId)}>Recharger</Button>
      </Box>

      {isPublic && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cette organisation est une personne morale de droit public. Le prélèvement
          n&apos;a pas lieu d&apos;être : la dépense est engagée, liquidée puis payée
          par virement du comptable public après service fait. L&apos;API refuse la
          création, le bouton n&apos;est donc pas proposé.
        </Alert>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Référence unique de mandat</TableCell>
              <TableCell>Débiteur</TableCell>
              <TableCell>Schéma</TableCell>
              <TableCell>IBAN</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Utilisable</TableCell>
              <TableCell>Signé le</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : mandates.length === 0 ? (
              <TableRow><TableCell colSpan={8}><Empty texte="Aucun mandat enregistré." /></TableCell></TableRow>
            ) : mandates.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{m.rum}</Typography></TableCell>
                <TableCell>{m.debtor_name}</TableCell>
                <TableCell>{m.scheme}</TableCell>
                <TableCell>{m.debtor_iban_last4 ? `xxxx ${m.debtor_iban_last4}` : NOT_PROVIDED}</TableCell>
                <TableCell>
                  <Chip size="small" label={m.status}
                    color={m.status === 'active' ? 'success' : m.status === 'revoked' ? 'error' : 'default'} />
                </TableCell>
                <TableCell>
                  {m.is_usable ? (
                    <Chip size="small" color="success" label="oui" />
                  ) : (
                    <Tooltip title={m.creditor_ics
                      ? 'Mandat non actif ou non signé'
                      : 'Identifiant créancier SEPA de Vigeos absent : aucune remise ne peut partir, même mandat actif'}>
                      <Chip size="small" label="non" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell><Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{dateFr(m.signed_at)}</Typography></TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Button size="small" disabled={m.status === 'revoked'} onClick={() => openItem('signer', m)}>Signature</Button>
                    <Button size="small" onClick={() => openItem('incident', m)}>Incident</Button>
                    <Button size="small" color="error" onClick={() => openItem('revoquer', m)}>Révoquer</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={Boolean(dialogue)} onClose={sending ? undefined : () => setDialogue(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogue?.type === 'creer' && 'Nouveau mandat de prélèvement'}
          {dialogue?.type === 'signer' && 'Enregistrer la signature'}
          {dialogue?.type === 'incident' && 'Enregistrer un incident'}
          {dialogue?.type === 'revoquer' && 'Révoquer le mandat'}
        </DialogTitle>
        <DialogContent dividers>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}

          {dialogue?.type === 'creer' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Aucun IBAN complet n&apos;est accepté : seuls les quatre derniers
                  chiffres sont conservés, ils suffisent à rapprocher un prélèvement
                  d&apos;un mandat.
                </Alert>
              </Grid>
              <Grid item xs={8}>
                <TextField fullWidth size="small" label="Nom du débiteur" value={form.debtor_name || ''}
                  onChange={(e) => setForm((f) => ({ ...f, debtor_name: e.target.value }))} />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Schéma</InputLabel>
                  <Select value={form.scheme || 'CORE'} label="Schéma"
                    onChange={(e) => setForm((f) => ({ ...f, scheme: e.target.value }))}>
                    <MenuItem value="CORE">CORE</MenuItem>
                    <MenuItem value="B2B">B2B</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Établissement rattaché</InputLabel>
                  <Select value={form.site_id || ''} label="Établissement rattaché"
                    onChange={(e) => setForm((f) => ({ ...f, site_id: e.target.value }))}>
                    <MenuItem value="">Aucun, mandat au niveau de l&apos;organisation</MenuItem>
                    {(orgs.find((o) => o.id === orgId)?.sites || []).map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Référence unique de mandat" value={form.rum || ''}
                  onChange={(e) => setForm((f) => ({ ...f, rum: e.target.value }))}
                  helperText="Vide : elle sera générée" />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="4 derniers chiffres de l'IBAN"
                  value={form.debtor_iban_last4 || ''} inputProps={{ maxLength: 4 }}
                  onChange={(e) => setForm((f) => ({ ...f, debtor_iban_last4: e.target.value }))}
                  error={Boolean(form.debtor_iban_last4) && form.debtor_iban_last4.length !== 4}
                  helperText="Exactement 4 caractères" />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="BIC" value={form.debtor_bic || ''}
                  inputProps={{ maxLength: 11 }}
                  onChange={(e) => setForm((f) => ({ ...f, debtor_bic: e.target.value }))} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Identifiant créancier SEPA" value={form.creditor_ics || ''}
                  onChange={(e) => setForm((f) => ({ ...f, creditor_ics: e.target.value }))}
                  helperText="Celui de Vigeos, obtenu auprès de la banque" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Notes" value={form.notes || ''}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </Grid>
            </Grid>
          )}

          {dialogue?.type === 'signer' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Le mandat passera au statut actif, mais il restera inutilisable tant
                  que l&apos;identifiant créancier SEPA de Vigeos est absent. Cette
                  action est rejouable pour l&apos;ajouter plus tard.
                </Alert>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="datetime-local" label="Date de signature"
                  InputLabelProps={{ shrink: true }} value={form.signed_at || ''}
                  onChange={(e) => setForm((f) => ({ ...f, signed_at: e.target.value }))}
                  helperText="Vide : maintenant" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Lieu de signature" value={form.signature_place || ''}
                  onChange={(e) => setForm((f) => ({ ...f, signature_place: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Clé du document signé" value={form.document_key || ''}
                  onChange={(e) => setForm((f) => ({ ...f, document_key: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Identifiant créancier SEPA" value={form.creditor_ics || ''}
                  onChange={(e) => setForm((f) => ({ ...f, creditor_ics: e.target.value }))} />
              </Grid>
            </Grid>
          )}

          {dialogue?.type === 'incident' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Nature de l'incident" value={form.kind || ''}
                  onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                  helperText="2 caractères au minimum" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Motif rendu par la banque" value={form.reason || ''}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  helperText="Champ libre : le code bancaire est repris tel quel, sans traduction" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Identifiant de facture concernée" value={form.invoice_id || ''}
                  onChange={(e) => setForm((f) => ({ ...f, invoice_id: e.target.value }))}
                  helperText="Facultatif. Un identifiant erroné passe en silence côté API" />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={Boolean(form.suspendre)}
                    onChange={(e) => setForm((f) => ({ ...f, suspendre: e.target.checked }))} />}
                  label="Suspendre le mandat" />
              </Grid>
            </Grid>
          )}

          {dialogue?.type === 'revoquer' && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Action irréversible</AlertTitle>
                Aucune route ne fait revenir un mandat révoqué, et la signature sera
                ensuite refusée. Il faudra en créer un nouveau et le faire signer.
              </Alert>
              <TextField fullWidth size="small" multiline rows={2} label="Motif de la révocation"
                value={form.reason || ''} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                helperText="3 caractères au minimum" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogue(null)} disabled={sending}>Annuler</Button>
          <Button variant="contained" color={dialogue?.type === 'revoquer' ? 'error' : 'primary'}
            disabled={!dialogValid || sending} onClick={validate}>
            {sending ? <CircularProgress size={18} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
